import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@components/common/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form"
import { useMutation, useQuery } from "@tanstack/react-query"
import { DictType } from "@pma/types"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import axios from "axios"
import { useForm } from "react-hook-form"
import {
  INVITE_USERS_ENDPOINT,
  SERVER_URL,
  USER_ROLE_DICTIONARY_ENDPOINT,
} from "@pma/utils/api-routes"
import { toast } from "@hooks/use-toast"
import { useModal } from "@stores/modal-store"
import { cn } from "@pma/utils/helpers"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { useAuth } from "@stores/auth-store"
import useTranslator from "@hooks/use-translator"
import { useEffect, useState } from "react"
import Recaptcha from "../auth/recaptcha"
import { useLocation } from "react-router-dom"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/common/dropdown-menu"

interface InviteUserFormProps extends React.HTMLAttributes<HTMLDivElement> {}

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Please enter a valid email" })
    .email({ message: "Invalid email address" }),
  user_role_id: z.optional(z.string()),
  user_name: z.string().min(2, { message: "Please enter a valid user name" }),
})

export default function InviteUsersModal({ className, ...props }: InviteUserFormProps) {
  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({
    v2: "",
    v3: "",
  })
  const [submittedData, setSubmittedData] = useState({})
  const [v2Fallback, setV2Fallback] = useState(false)

  const { translate } = useTranslator()
  const modal = useModal()

  const defaultUser = modal?.metaData

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const currentPath = useLocation().pathname

  const { authConfig } = useAuth()

  const orgId = authConfig?.last_organization_id
  const [selectedRole, setSelectedRole] = useState<{ id: number; name: string } | null>(null)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      user_name: "",
    },
  })

  const sendResetLink = async (payload: DictType) => {
    if (!v2Fallback && !recaptchaToken.v3) {
      return toast({
        title: translate("Info"),
        description: translate("Please complete the reCAPTCHA"),
        variant: "info",
      })
    }

    if ((v2Fallback && !payload?.v2Token) || !recaptchaToken.v3) {
      return
    }

    const isLocalHost = process.env.NODE_ENV === "development"
    const isDev = currentPath.includes("https://rog-tech.io/dev.pms")
    const originEnvironment = isDev
      ? {
          for_dev: true,
          for_test: false,
        }
      : isLocalHost
        ? {
            for_test: true,
          }
        : {
            for_test: false,
            for_dev: false,
          }

    const response = await axios.post(
      SERVER_URL + INVITE_USERS_ENDPOINT,
      {
        ...payload,
        ...originEnvironment,
        recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
        recaptcha_response_v2: payload?.v2Token,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const sendEmailMutation = useMutation({
    mutationFn: sendResetLink,
    mutationKey: ["invite-link"],
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: translate("Invite sent successfully"),
        variant: "success",
      })

      closeModal()
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "Invalid Captcha Response. Please try again.") {
        setV2Fallback(true)
      } else {
        toast({
          title: translate("Error"),
          description: translate(errorMessage || "Unable to send invite"),
          variant: "error",
        })
      }
    },
  })

  const getRoles = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${USER_ROLE_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data } = useQuery({
    queryKey: ["roles", orgId],
    queryFn: getRoles,
  })

  function onSubmit(data: z.infer<typeof formSchema>) {
    // Track for v2 resubmission
    setSubmittedData(data)
    sendEmailMutation.mutate({ ...data, organization_id: orgId })
  }

  const reSubmit = (token: string) => {
    // sendEmailMutation.mutate({ ...submittedData, v2Token: token })
    location.reload()
  }

  const closeModal = () => {
    // reset modal store
    modal.onClose()
    modal.setContext(undefined)
    modal.setMetaData({})
    form.setValue("email", "")
    form.setValue("user_role_id", "")
    form.setValue("user_name", "")
    setSelectedRole(null)
  }

  useEffect(() => {
    if (defaultUser?.email) {
      form.setValue("email", defaultUser?.email)
      form.setValue("user_name", "")
    } else {
      form.setValue("email", "")
      form.setValue("user_name", "")
    }

    return () => {
      form.setValue("email", "")
      form.setValue("user_name", "")
    }
  }, [defaultUser?.email])

  const handleDropDown = (val: any) => {
    setSelectedRole({ id: val.id, name: val.name })
    form.setValue("user_role_id", val.id.toString())
  }

  return (
    <Dialog open={modal.isOpen && modal.context === "invite"} onOpenChange={closeModal}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className='flex flex-col items-center justify-center gap-y-4 pb-2'>
            <h1 className='text-2xl font-semibold tracking-tight'>
              {translate("Invite member to workspace")}{" "}
            </h1>
          </DialogTitle>
          <DialogDescription className='dark:text-white-fade space-y-2 pt-2 text-center font-medium text-zinc-900'>
            <div className={cn("grid gap-6 text-muted-foreground", className)} {...props}>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className='grid gap-2'>
                    <FormField
                      control={form.control}
                      name='email'
                      render={({ field }) => (
                        <FormItem className='flex flex-col items-start justify-start space-y-2'>
                          <FormLabel className='capitalize'>{translate("Email")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder='name@example.com'
                              className='rounded-full text-black'
                              {...field}
                              autoFocus
                              disabled={defaultUser?.email}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='user_name'
                      render={({ field }) => (
                        <FormItem className='flex flex-col items-start justify-start space-y-2'>
                          <FormLabel className='capitalize'>{translate("User Name")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={translate("User name")}
                              className='rounded-full text-black'
                              {...field}
                              disabled={defaultUser?.user_name}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className=''>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='outline'
                            size='sm'
                            className='w-full text-black data-[state=open]:bg-accent'
                          >
                            <span>
                              {selectedRole === null ? translate("Select role") : selectedRole.name}
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='center'>
                          {data &&
                            data.length > 0 &&
                            data.map((val: any) => (
                              <DropdownMenuItem
                                key={val.id}
                                id={val.id}
                                onClick={() => handleDropDown({ id: val.id, name: val.name })}
                              >
                                {val.name}
                              </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <Button
                      className='mt-2'
                      loading={sendEmailMutation.isPending}
                      disabled={sendEmailMutation.isPending}
                    >
                      {translate("Send Invite")}{" "}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div className='flex w-full flex-col items-center justify-center'>
            <div className='flex w-full text-xs text-slate-500'>
              <span>{translate("Recipient will receive an email link")}. </span>
            </div>
          </div>
        </DialogFooter>

        {/* GOOGLE Recaptcha */}
        <Recaptcha setToken={setRecaptchaToken} v2={v2Fallback} reSubmit={reSubmit} />
      </DialogContent>
    </Dialog>
  )
}
